import { AppData, Lightning, Utils } from "@lightningjs/sdk";
import { Artwork, Column } from "@lightningjs/ui-components";
import ChannelListItem, { FOCUSED_H } from "./channel-list-item";
import { getImage } from "../../../models/data-service";
import { Helper } from "../../../helper";
import { AppSetup } from "../../../app-setup";
import EPGUtility from "../epg-utility";
import { REQUEST_STATUS } from "../../../enum";
import { EPGRatingBadge } from "./epg-guide";

const PROGRAM_TITLE_TWO_LINE_H = 85;
const START_TIME_BASE_Y = 657;
const PROGRESS_TRACK_BASE_Y = 686;
const DESCRIPTION_BASE_Y = 710;

export default class ChannelListOverlay extends Lightning.Component {

    private _channelCount = 0;
    private _channels: any[] = [];
    private _thumbUrl = '';
    private _programImageUrl = '';
    private _epgManager: any = null;
    private _filteredEpg: any[] = [];
    private _epgCurrDate: Date | null = null;
    private _fetchToken = 0;
    private _prevEpgGlobalIndex = -1;
    private _fetchDebounceTimer: any = null;
    private _thumbnailDebounceTimer: any = null;
    private _lastNavTime = 0;
    private _navThrottleMs = 300;


    static override _template(): Lightning.Component.Template<Lightning.Component.TemplateSpecLoose> {
        return {
            w: 1920,
            h: 1080,
            rect: true,
            color: 0xe611113b,
            collision: 2 as any,
            ChannelList: {
                x: 56,
                y: 0,
                w: 1078,
                h: 1080,
                clipping: false,
                zIndex: 1,
                collision: 2,
                ChannelColumn: {
                    y: 134,
                    w: 1078,
                    h: 946,      // 1080 - 134
                    type: Column,
                    zIndex: 1,
                    collision: 2,
                    style: { itemSpacing: 10 }
                }
            },
            RightPanel: {
                x: 1233,   // list end (1134) + margin-left (99)
                y: 110,    // margin-top
                w: 687,
                h: 789,
                rect: true,
                color: 0x00000000,
                Header: {
                    x: 0,
                    y: 0,
                    w: 538,
                    h: 92,
                    rect: true,
                    color: 0x00000000,
                    Thumbnail: {
                        x: 0,
                        y: 0,
                        w: 164,
                        h: 92,
                        rect: true,
                        color: 0x00000000,
                        shader: { type: Lightning.shaders.RoundedRectangle, radius: 10 },
                        Img: {
                            x: 0,
                            y: 0,
                            w: 164,
                            h: 92,
                            type: Artwork,
                            src: '',
                            style: { radius: 10 }
                        }
                    },
                    ChannelName: {
                        x: 184,
                        y: 0,
                        w: 354,
                        text: {
                            text: '',
                            fontSize: 30,
                            fontWeight: 700,
                            textColor: 0xffffffff,
                            fontFace: Helper.getFontFamily('Black'),
                            wordWrapWidth: 354,
                            maxLines: 1,
                            textOverflow: 'ellipsis'
                        }
                    },
                    NowPlayingBadge: {
                        type: EPGRatingBadge,
                        x: 184,
                        y: 46,
                        title: 'Now Playing',
                        style: {
                            minWidth: 149,
                            minHeight: 33,
                            paddingX: 12,
                            paddingY: 4,
                            radius: 10,
                            strokeWidth: 1,
                            strokeColor: 0xff30cf58,
                            backgroundColor: 0xe60d0d1d,
                            textStyle: { textColor: 0xff30cf58, fontSize: 16, fontWeight: 500, fontFace: Helper.getFontFamily('Regular') }
                        }
                    }
                },
                ProgramImage: {
                    x: 0,
                    y: 123,   // header (92) + margin-top (31)
                    w: 630,
                    h: 354,
                    rect: true,
                    color: 0x00000000,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 20 },
                    Img: {
                        x: 0,
                        y: 0,
                        w: 630,
                        h: 354,
                        type: Artwork,
                        src: '',
                        style: { radius: 20 }
                    }
                },
                GenreRating: {
                    x: 0,
                    y: 508,   // program image bottom (477) + category margin-top (31)
                    w: 687,
                    h: 35,
                    flex: { direction: 'row', alignItems: 'center' },
                    RatingBadge: {
                        type: EPGRatingBadge,
                        title: '',
                        style: {
                            minWidth: 91,
                            minHeight: 40,
                            paddingX: 12,
                            paddingY: 4,
                            radius: 10,
                            strokeWidth: 1,
                            strokeColor: 0x80ffffff,
                            backgroundColor: 0xe60d0d1d,
                            textStyle: { textColor: 0xffffffff, fontSize: 18, fontFace: Helper.getFontFamily('Regular') }
                        },
                        visible: false
                    },
                    CategoryText: {
                        w: 578,
                        h: 35,
                        alpha: 0.8,
                        text: {
                            text: '',
                            fontSize: 24,
                            fontWeight: 500,
                            textColor: 0xffffffff,
                            wordWrapWidth: 578,
                            maxLines: 1,
                            textOverflow: 'ellipsis'
                        }
                    }
                },
                ProgramTitle: {
                    x: 0,
                    y: 561,
                    w: 666,
                    text: {
                        text: '',
                        fontSize: 36,
                        fontFace: Helper.getFontFamily('Black'),
                        textColor: 0xffffffff,
                        textAlign: 'left',
                        wordWrapWidth: 666,
                        maxLines: 2,
                        textOverflow: 'ellipsis'
                    }
                },
                ProgressTrack: {
                    x: 0,
                    y: 686,   // ProgramTitle bottom (646) + mt-35
                    w: 630,
                    h: 3,
                    rect: true,
                    color: 0x80ffffff,
                    ProgressFill: {
                        x: 0, y: 0, w: 0, h: 3,
                        rect: true,
                        color: 0xffffffff
                    }
                },
                StartTime: {
                    x: 0,
                    y: 657,   // above track (681) with 24px for text height
                    text: {
                        text: '',
                        fontSize: 16,
                        textColor: 0xffffffff
                    }
                },
                EndTime: {
                    x: 0,
                    y: 657,
                    w: 630,
                    text: {
                        text: '',
                        fontSize: 16,
                        textColor: 0xffffffff,
                        textAlign: 'right',
                        wordWrapWidth: 630
                    }
                },
                Description: {
                    x: 0,
                    y: 710,   // time labels bottom (~710) + mt-20
                    w: 573,   // 630 - 57px right margin from CSS
                    h: 73,
                    text: {
                        text: '',
                        fontSize: 24,
                        fontWeight: 500,
                        textColor: 0xffffffff,
                        wordWrapWidth: 573,
                        lineHeight: 37,   // 24 * 1.54 from CSS line-height
                        maxLines: 2,
                        textOverflow: 'ellipsis'
                    }
                }
            },
            // Bottom gradient footer with "Back to [Channel]" affordance.
            Footer: {
                x: 0,
                y: 863,   // 1080 - 217
                w: 1920,
                h: 217,
                zIndex: 3,
                collision: 2,
                GradientPart: {
                    x: 0,
                    y: 0,
                    w: 1920,
                    h: 141,     // top 65% — fades from transparent to solid
                    rect: true,
                    colorTop: 0x000D0D1D,
                    colorBottom: 0xCC0D0D1D
                },
                SolidPart: {
                    x: 0,
                    y: 141,
                    w: 1920,
                    h: 76,      // bottom 35% — solid rgba(13,13,29,0.8)
                    rect: true,
                    color: 0xCC0D0D1D
                },
                BackButton: {
                    x: 132,
                    y: 109,     // 56px from screen bottom: 1080 - 56 - 52 - 863
                    w: 586,
                    h: 52,
                    collision: true,
                    zIndex: 4,
                    Arrow: {
                        x: 0,
                        y: 0,
                        texture: {
                            src: Utils.asset('images/ico-back.png'),
                            resizeMode: { h: 52, w: 52, type: 'contain' },
                            type: Lightning.textures.ImageTexture
                        }
                    },
                    Label: {
                        x: 67,
                        y: 4,
                        w: 467,
                        h: 39,
                        text: {
                            text: '',
                            fontSize: 26,
                            fontWeight: 700,
                            fontFace: Helper.getFontFamily('Regular'),
                            textColor: 0xffffffff,
                            wordWrapWidth: 467,
                            maxLines: 1,
                            textOverflow: 'ellipsis',
                            lineHeight: 40
                        }
                    }
                }
            },
            // Header gradient renders on top of the list (zIndex:2) so it creates a
            // natural fade over the first items, matching the CSS spec.
            Header: {
                x: 0,
                y: 0,
                w: 1920,
                h: 176,
                zIndex: 2,
                SolidPart: {
                    x: 0,
                    y: 0,
                    w: 1920,
                    h: 62,       // 35% of 176 — matches CSS 35% stop
                    rect: true,
                    color: 0xCC0D0D1D
                },
                GradientPart: {
                    x: 0,
                    y: 62,
                    w: 1920,
                    h: 114,      // 65% of 176 — fades to transparent
                    rect: true,
                    colorTop: 0xCC0D0D1D,
                    colorBottom: 0x000D0D1D
                },
                Title: {
                    x: 56,
                    y: 47,
                    w: 600,
                    h: 53,
                    text: {
                        text: 'Channels',
                        fontSize: 44,
                        fontFace: Helper.getFontFamily('Black'),
                        textColor: 0xffffffff,
                        wordWrapWidth: 600,
                        maxLines: 1
                    }
                }
            }
        }
    }

    static get __componentName(): string {
        return 'ChannelListOverlay'
    }

    override _init() {
        const col = (this as any).tag('ChannelList.ChannelColumn');
        col.scrollTransition = { duration: 0 };
        col.scrollIndex = 3;

        const pt = (this as any).tag('RightPanel.ProgramTitle');
        pt?.on('txLoaded', () => this._alignBelowTitle());
    }

    private _alignBelowTitle() {
        const s = this as any;
        const pt = s.tag('RightPanel.ProgramTitle');
        if (!pt) return;
        const titleH = pt.renderHeight || PROGRAM_TITLE_TWO_LINE_H;
        const delta = Math.max(0, PROGRAM_TITLE_TWO_LINE_H - titleH);
        s.tag('RightPanel.StartTime').y = START_TIME_BASE_Y - delta;
        s.tag('RightPanel.EndTime').y = START_TIME_BASE_Y - delta;
        s.tag('RightPanel.ProgressTrack').y = PROGRESS_TRACK_BASE_Y - delta;
        s.tag('RightPanel.Description').y = DESCRIPTION_BASE_Y - delta;
    }

    private _canNavigate(): boolean {
        const now = Date.now();
        if (now - this._lastNavTime < this._navThrottleMs) return false;
        this._lastNavTime = now;
        return true;
    }

    set channelCount(val: number) {
        this._channelCount = val;
        (this as any).tag('Header.Title').text.text = `Channels (${val})`;
    }

    get channelCount(): number {
        return this._channelCount;
    }

    set currentChannelName(name: string) {
        const label = name ? `Back to ${name}` : 'Back';
        (this as any).tag('Footer.BackButton.Label').text.text = label;
    }

    setChannels(channels: any[], initialIndex = 0) {
        this._channels = channels;
        this._thumbUrl = '';
        this._programImageUrl = '';
        const focusIdx = Math.max(0, Math.min(initialIndex, channels.length - 1));
        this._buildItems(focusIdx);
        this.channelCount = channels.length;
        const ch = channels[focusIdx] ?? channels[0] ?? {};
        this._updateThumbnail(ch.mainPicture, ch.channelName, ch.programImage, ch.rating, ch.category, ch.programName, ch.description, ch.startTime, ch.endTime, ch.progress);
    }

    setEpgContext(epgManager: any, filteredEpg: any[], currDate: Date) {
        this._epgManager = epgManager;
        this._filteredEpg = filteredEpg;
        this._epgCurrDate = currDate;
        this._prevEpgGlobalIndex = (filteredEpg[0]?.index ?? 0) - 1;
        // Fetch the first visible batch immediately and refresh when done.
        if (filteredEpg[0]?.index !== undefined) {
            epgManager.fetchProgramDetails(epgManager.EPGChannelList, filteredEpg[0].index)
                .then(() => this._refreshBatch());
        }
    }

    $channelItemFocused(index: number) {
        // Blur immediately on navigation — only if URL changes AND a previous image exists
        const s = this as any;
        const ch = this._channels[index];
        if (ch?.mainPicture && ch.mainPicture !== this._thumbUrl && this._thumbUrl) {
            const thumbNode = s.tag('RightPanel.Header.Thumbnail.Img');
            if (thumbNode) thumbNode.blur = true;
        }
        if (ch?.programImage && ch.programImage !== this._programImageUrl && this._programImageUrl) {
            const progNode = s.tag('RightPanel.ProgramImage.Img');
            if (progNode) progNode.blur = true;
        }

        // Always debounce the right-panel update — reads latest _channels[index] at fire time
        // so any _refreshBatch() data written during the 350ms window is picked up.
        clearTimeout(this._thumbnailDebounceTimer);
        this._thumbnailDebounceTimer = setTimeout(() => {
            const latest = this._channels[index];
            this._updateThumbnail(latest?.mainPicture, latest?.channelName, latest?.programImage, latest?.rating, latest?.category, latest?.programName, latest?.description, latest?.startTime, latest?.endTime, latest?.progress);
        }, 500);

        if (!this._epgManager || !this._filteredEpg.length) return;

        const epgCh = this._filteredEpg[index];
        if (!epgCh) return;

        const globalIdx = epgCh.index ?? index;

        // Same directional prefetch the EPG guide uses — fire-and-forget.
        this._epgManager.getNextPrefetchingIndex(globalIdx, this._prevEpgGlobalIndex);
        this._prevEpgGlobalIndex = globalIdx;

        const status = this._epgManager.EPGChannelList[globalIdx]?.fetchStatus;
        if (status === REQUEST_STATUS.OK || status === REQUEST_STATUS.FAIL) {
            this._refreshBatch();
            return;
        }

        // Debounce: don't fire a fetch for every channel the user scrolls past.
        const token = ++this._fetchToken;
        clearTimeout(this._fetchDebounceTimer);
        this._fetchDebounceTimer = setTimeout(async () => {
            if (token !== this._fetchToken) return;
            const latest = this._epgManager.EPGChannelList[globalIdx]?.fetchStatus;
            if (latest === REQUEST_STATUS.OK || latest === REQUEST_STATUS.FAIL) {
                this._refreshBatch();
                return;
            }
            await this._epgManager.fetchProgramDetails(this._epgManager.EPGChannelList, globalIdx);
            if (token !== this._fetchToken) return;
            this._refreshBatch();
        }, 300);
    }

    // After any fetch completes: update _channels[] for every loaded channel,
    // then patch the live ChannelListItem components directly via their refs
    // (no column rebuild needed — avoids focus side-effects and flicker).
    private _refreshBatch() {
        const currDate = this._epgCurrDate ?? new Date();
        const col = (this as any).tag('ChannelList.ChannelColumn');

        this._filteredEpg.forEach((epgCh, i) => {
            if (!epgCh || !this._channels[i]) return;
            const globalIdx = epgCh.index ?? i;
            const status = this._epgManager?.EPGChannelList[globalIdx]?.fetchStatus;
            if (status !== REQUEST_STATUS.OK) return;

            const program = epgCh.getNowPlayingProgram(currDate);
            if (!program || program.title === 'No Data') return;

            let progress = 0;
            if (program.start_timeObj && program.end_timeObj) {
                const dur = program.end_timeObj.getTime() - program.start_timeObj.getTime();
                const elapsed = currDate.getTime() - program.start_timeObj.getTime();
                progress = dur > 0 ? Math.max(0, Math.min(1, elapsed / dur)) : 0;
            }

            const ch = this._channels[i];
            this._channels[i] = {
                ...ch,
                programName: program.title || ch.programName,
                programImage: program.image || ch.programImage,
                rating: program.rating || ch.rating,
                category: program.category || ch.category,
                description: program.description || ch.description,
                startTime: program.start_timeObj ? EPGUtility.formatAMPM(program.start_timeObj) : ch.startTime,
                endTime: program.end_timeObj ? EPGUtility.formatAMPM(program.end_timeObj) : ch.endTime,
                progress
            };

            // Patch the live ChannelListItem directly — no rebuild required.
            const item = col?.tag(`Item${i}`);
            if (item) {
                item.programName = program.title;
                item.progress = progress;
            }
        });
    }

    private _updateThumbnail(url: string, channelName?: string, programImage?: string, rating?: string, category?: string, programName?: string, description?: string, startTime?: string, endTime?: string, progress?: number) {
        const s = this as any;
        if (channelName !== undefined) {
            s.tag('RightPanel.Header.ChannelName').text.text = channelName;
        }
        if (url && url !== this._thumbUrl) {
            this._thumbUrl = url
            const thumbNode = s.tag('RightPanel.Header.Thumbnail.Img')
            if (thumbNode) {
                thumbNode.src = getImage(this._thumbUrl)
                thumbNode.signals = {
                    'imageLoaded': () => {
                        thumbNode.blur = false
                        thumbNode.signals = {}
                    }
                }
            }
        }
        if (programImage && programImage !== this._programImageUrl) {
            this._programImageUrl = programImage
            const progNode = s.tag('RightPanel.ProgramImage.Img')
            if (progNode) {
                progNode.src = getImage(this._programImageUrl)
                progNode.signals = {
                    'imageLoaded': () => {
                        progNode.blur = false
                        progNode.signals = {}
                    }
                }
            }
        }
        if (rating) {
            s.tag('RightPanel.GenreRating.RatingBadge').patch({ title: rating, visible: true });
            s.tag('RightPanel.GenreRating.CategoryText').patch({ flexItem: { marginLeft: 18 } });
        } else {
            s.tag('RightPanel.GenreRating.RatingBadge').visible = false;
            s.tag('RightPanel.GenreRating.CategoryText').patch({ flexItem: { marginLeft: 0 } });
        }
        if (category !== undefined) {
            s.tag('RightPanel.GenreRating.CategoryText').text.text = category;
        }
        if (programName !== undefined) {
            const pt = s.tag('RightPanel.ProgramTitle');
            if (pt) pt.text.text = programName;
        }
        if (progress !== undefined) {
            const { appState } = AppData as AppSetup;
            const focusColor = Helper.getHexaColor(
                appState?.getAppApiConfig()?.appLayoutConfig?.textfocuscolor
            ) || 0xffffffff;
            const fillW = Math.round(Math.max(0, Math.min(1, progress)) * 630);
            s.tag('RightPanel.ProgressTrack.ProgressFill').patch({ w: fillW, color: focusColor });
        }
        if (startTime !== undefined) {
            s.tag('RightPanel.StartTime').text.text = startTime;
        }
        if (endTime !== undefined) {
            s.tag('RightPanel.EndTime').text.text = endTime;
        }
        if (description !== undefined) {
            s.tag('RightPanel.Description').text.text = description;
        }
    }

    private _buildItems(focusedIdx: number) {
        const items = this._channels.map((ch, i) => ({
            type: ChannelListItem,
            ref: `Item${i}`,
            w: 1055,
            h: i === focusedIdx ? FOCUSED_H : 102,
            itemIndex: i,
            iconUrl: ch.iconUrl || '',
            channelName: ch.channelName || '',
            programName: ch.programName || '',
            progress: ch.progress ?? 0
        }));

        const col = (this as any).tag('ChannelList.ChannelColumn');
        col.items = items;
        if (focusedIdx >= 0) col.selectedIndex = focusedIdx;
    }

    override _detach() {
        clearTimeout(this._fetchDebounceTimer);
        clearTimeout(this._thumbnailDebounceTimer);
    }

    override _getFocused() {
        return (this as any).tag('ChannelList.ChannelColumn') || this;
    }

    $channelItemEnter(index: number) {
        (this as any).fireAncestors('$channelListSelectChannel', index);
    }

    override _handleBack() {
        (this as any).fireAncestors('$closeChannelList');
        return true;
    }

    _handleClick(target: any) {
        if (target?.ref !== 'BackButton') return false;
        this._handleBack();
        return true;
    }

    $navigateChannelDown() {
        if (!this._canNavigate()) return;
        const col = (this as any).tag('ChannelList.ChannelColumn');
        col.selectNext(false);
        col._update();
    }

    $navigateChannelUp() {
        if (!this._canNavigate()) return;
        const col = (this as any).tag('ChannelList.ChannelColumn');
        col.selectPrevious();
        col._update();
    }

    _handleScroll(obj: any) {
        if (obj?.wheelDeltaY > 0) {
            this.$navigateChannelUp();
        } else {
            this.$navigateChannelDown();
        }
        return true;
    }

    // Boundary traps — prevent Up/Down from bubbling to VideoScreenLive at list edges.
    override _handleDown() {
        return true;
    }

    override _handleUp() {
        return true;
    }

    override _handleLeft() {
        return true;
    }

    override _handleRight() {
        return true;
    }
}
